import { toast } from '@affine/component';
import {
  GraphQLService,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import type { WORKSPACE_DIALOG_SCHEMA } from '@affine/core/modules/dialogs/constant';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs/services/workspace-dialog';
import type { DialogProps } from '@affine/core/modules/dialogs/types';
import {
  OFFLINE_DELETE_COPY_CONFIRMATION,
  OFFLINE_DELETE_TABLE_CHOICE,
} from '@affine/core/modules/offline-mutation-outbox/services/offline-mutation-error';
import {
  OfflineMutationDomainError,
  type OfflineMutationOutboxService,
} from '@affine/core/modules/offline-mutation-outbox/services/offline-mutation-outbox';
import type { OfflineMutationRecord } from '@affine/core/modules/offline-mutation-outbox/types';
import type { AccessibleFolderCacheStore } from '@affine/core/modules/organize/stores/accessible-folder-cache';
import type { WorkspacePermissionService } from '@affine/core/modules/permissions/services/permission';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  type AppBaseTableDispositionInput,
  appContainersForDeletionQuery,
  mineCopyQuery,
  MineCopySourceKind,
  publishAppContainerToMineMutation,
  publishAutomationToMineMutation,
  publishWorkflowToMineMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { Service } from '@toeverything/infra';

export interface ContainerCopySource {
  appBaseId: string;
  title: string;
  hasMountedApps: boolean;
}
type PermanentOperation =
  | 'doc.permanentDelete'
  | 'folder.permanentDelete'
  | 'appBase.permanentDelete';
export type PermanentDeleteRecord = {
  [Op in PermanentOperation]: OfflineMutationRecord<Op>;
}[PermanentOperation];
type AutomationTrashRecord = OfflineMutationRecord<'automation.trash'>;
type CopyQuestion = DialogProps<WORKSPACE_DIALOG_SCHEMA['delete-with-copy']>;

/** An automation (by block id) or workflow about to be deleted. */
export interface FlowCopySource {
  kind: 'automation' | 'workflow';
  sourceId: string;
  title: string;
}

export function appBaseIdFromManagedFolder(
  workspaceId: string,
  folder: { id: string; systemKind?: string | null }
): string | null {
  if (folder.systemKind !== 'app_base_folder') return null;
  const prefix = `app_base_${workspaceId}_`;
  return folder.id.startsWith(prefix) ? folder.id.slice(prefix.length) : null;
}

export function needsCopyConfirmation(
  record: OfflineMutationRecord
): record is PermanentDeleteRecord | AutomationTrashRecord {
  return (
    ((record.operation === 'doc.permanentDelete' ||
      record.operation === 'folder.permanentDelete' ||
      record.operation === 'appBase.permanentDelete' ||
      record.operation === 'automation.trash') &&
      record.lastError === OFFLINE_DELETE_COPY_CONFIRMATION) ||
    ((record.operation === 'doc.permanentDelete' ||
      record.operation === 'appBase.permanentDelete') &&
      record.lastError === OFFLINE_DELETE_TABLE_CHOICE)
  );
}

/** The copy question runs outside the outbox executor so unrelated targets can send. */
export class ContainerCopyBeforeDeleteService extends Service {
  private processing = false;
  private disposed = false;

  constructor(
    private readonly workspaceServer: WorkspaceServerService,
    private readonly dialogs: WorkspaceDialogService,
    private readonly permission: WorkspacePermissionService,
    private readonly workspaceService: WorkspaceService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly folders: AccessibleFolderCacheStore
  ) {
    super();
    const failed = outbox.failedMutations$.subscribe(() =>
      this.scheduleQuestions()
    );
    const dialogsChanged = dialogs.dialogs$.subscribe(() =>
      this.scheduleQuestions()
    );
    this.disposables.push(() => {
      this.disposed = true;
      failed.unsubscribe();
      dialogsChanged.unsubscribe();
    });
  }

  // GraphQLService lives in the server scope; a workspace reaches it through
  // its server (27.10.: the app did not start, missing dependency in the
  // workspace scope)
  private get graphql(): GraphQLService {
    const server = this.workspaceServer.server;
    if (!server) throw new Error('No server');
    return server.scope.get(GraphQLService);
  }

  async deletionSources(input: {
    docIds?: string[];
    appBaseIds?: string[];
  }): Promise<ContainerCopySource[]> {
    const result = await this.graphql.gql({
      query: appContainersForDeletionQuery,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docIds: input.docIds,
        appBaseIds: input.appBaseIds,
      },
    });
    return result.appContainersForDeletion;
  }

  async confirmContainers(
    sources: readonly ContainerCopySource[],
    confirmName?: string,
    tableChoices?: CopyQuestion['tableChoices']
  ): Promise<boolean> {
    const unique = [
      ...new Map(sources.map(source => [source.appBaseId, source])).values(),
    ];
    if (unique.length === 0) return true;
    return this.ask({
      kind: 'container',
      title: unique.map(source => source.title).join(', '),
      canPublish: this.permission.permission.isOwnerOrAdmin$.value === true,
      unavailableReason: unique.some(source => !source.hasMountedApps)
        ? I18n.t('com.streamlain.marketplace.container.empty')
        : undefined,
      confirmName,
      tableChoices,
      publishCopy: async () => {
        let templateId = '';
        for (const source of unique) {
          const result = await this.graphql.gql({
            query: publishAppContainerToMineMutation,
            variables: {
              workspaceId: this.workspaceService.workspace.id,
              appBaseId: source.appBaseId,
              fromTrash: true,
            },
          });
          templateId = result.publishAppContainerToMine.templateId;
        }
        return { templateId };
      },
    });
  }

  /**
   * The copy-to-Mine question before an automation or workflow is deleted
   * (dec-wfy-trdb-copy-mine-path-1026). An existing copy is named with its
   * date and Yes adds a new version to it. Resolves `true` when the delete
   * may proceed, `null` when the lookup cannot reach the server.
   */
  async confirmFlow(source: FlowCopySource): Promise<boolean | null> {
    const question = await this.flowQuestion(source);
    return typeof question === 'object' && question !== null
      ? this.ask(question)
      : question;
  }

  /**
   * The copy question for a source, `true` when there is nothing to ask
   * (never saved on the server), `null` when the server cannot be reached.
   */
  private async flowQuestion(
    source: FlowCopySource
  ): Promise<CopyQuestion | true | null> {
    const canPublish =
      this.permission.permission.isOwnerOrAdmin$.value === true;
    let existingCopy: CopyQuestion['existingCopy'];
    if (canPublish) {
      let lookup;
      try {
        lookup = await this.lookupFlowCopy(source);
      } catch (error) {
        if (UserFriendlyError.fromAny(error).isNetworkError()) return null;
        throw error;
      }
      // Never saved on the server: there is nothing to copy.
      if (!lookup.sourceExists) return true;
      existingCopy = lookup.copy ?? undefined;
    }
    return {
      kind: source.kind,
      title: source.title,
      canPublish,
      existingCopy,
      publishCopy: () => this.publishFlowCopy(source),
    };
  }

  private questionOpen(): boolean {
    return this.dialogs.dialogs$.value.some(
      dialog => dialog.type === 'delete-with-copy'
    );
  }

  /**
   * A trash queued while the copy lookup could not reach the server asks the
   * question before it is sent, if there is a source to copy.
   */
  async checkAutomationTrashBeforeSend(
    record: AutomationTrashRecord
  ): Promise<void> {
    if (record.payload.copyConfirmed !== false) return;
    if (this.permission.permission.isOwnerOrAdmin$.value !== true) return;
    const lookup = await this.lookupFlowCopy({
      kind: 'automation',
      sourceId: record.payload.blockId,
    });
    if (lookup.sourceExists) {
      throw new OfflineMutationDomainError(OFFLINE_DELETE_COPY_CONFIRMATION);
    }
  }

  private ask(question: CopyQuestion): Promise<boolean> {
    if (this.questionOpen()) return Promise.resolve(false);
    return new Promise(resolve => {
      this.dialogs.open('delete-with-copy', question, result =>
        resolve(result === true)
      );
    });
  }

  private async lookupFlowCopy({
    kind,
    sourceId,
  }: Pick<FlowCopySource, 'kind' | 'sourceId'>) {
    const result = await this.graphql.gql({
      query: mineCopyQuery,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        sourceKind:
          kind === 'automation'
            ? MineCopySourceKind.AUTOMATION
            : MineCopySourceKind.WORKFLOW,
        sourceId,
      },
    });
    return result.mineCopy;
  }

  private async publishFlowCopy({
    kind,
    sourceId,
  }: FlowCopySource): Promise<{ templateId: string }> {
    const workspaceId = this.workspaceService.workspace.id;
    if (kind === 'automation') {
      const result = await this.graphql.gql({
        query: publishAutomationToMineMutation,
        variables: { workspaceId, automationBlockId: sourceId },
      });
      return { templateId: result.publishAutomationToMine.templateId };
    }
    const result = await this.graphql.gql({
      query: publishWorkflowToMineMutation,
      variables: { workspaceId, workflowId: sourceId },
    });
    return { templateId: result.publishWorkflowToMine.templateId };
  }

  /** A queued automation trash asks once it can, then goes or stays. */
  private async askAutomationTrash(
    record: AutomationTrashRecord
  ): Promise<void> {
    let question: CopyQuestion | true | null;
    try {
      question = await this.flowQuestion({
        kind: 'automation',
        sourceId: record.payload.blockId,
        title: record.payload.displayName ?? '',
      });
    } catch {
      return;
    }
    if (question === null || this.disposed) return;
    // Another copy question is open: this one waits, its close asks again.
    if (question !== true && this.questionOpen()) return;
    const confirmed = question === true || (await this.ask(question));
    if (this.disposed) return;
    await this.outbox.removeFailed(record.id);
    if (confirmed) {
      await this.outbox.enqueue({
        operation: record.operation,
        payload: { ...record.payload, copyConfirmed: true },
        idempotencyKey: record.idempotencyKey,
        dedupePending: true,
        orderKey: record.orderKey,
      });
    } else {
      toast(I18n.t('com.streamlain.trash.automation-trash-cancelled'));
    }
  }

  async checkBeforeSend(record: PermanentDeleteRecord): Promise<void> {
    if (record.payload.copyConfirmed) return;
    if ((await this.sourcesFor(record)).length > 0) {
      throw new OfflineMutationDomainError(OFFLINE_DELETE_COPY_CONFIRMATION);
    }
  }

  private sourcesFor(
    record: PermanentDeleteRecord
  ): Promise<ContainerCopySource[]> {
    if (record.operation === 'doc.permanentDelete')
      return this.deletionSources({ docIds: [record.payload.docId] });
    if (record.operation === 'appBase.permanentDelete')
      return this.deletionSources({ appBaseIds: [record.payload.appBaseId] });
    const folder = this.folders.getCachedFolder(record.payload.folderId);
    const appBaseId =
      folder && appBaseIdFromManagedFolder(record.workspaceId, folder);
    return this.deletionSources({
      docIds: record.payload.affectedDocIds ?? [],
      appBaseIds: appBaseId ? [appBaseId] : [],
    });
  }

  private scheduleQuestions(): void {
    if (this.processing || this.disposed) return;
    queueMicrotask(() => {
      void this.askPending().catch(console.error);
    });
  }

  private async askPending(): Promise<void> {
    if (this.processing || this.disposed || this.questionOpen()) return;
    this.processing = true;
    try {
      const attempted = new Set<string>();
      while (true) {
        const record = this.outbox.failedMutations$.value
          .filter(needsCopyConfirmation)
          .find(record => !attempted.has(record.id));
        if (!record) return;
        attempted.add(record.id);
        if (this.disposed) return;
        if (record.operation === 'automation.trash') {
          await this.askAutomationTrash(record);
          continue;
        }
        let sources: ContainerCopySource[];
        try {
          sources = await this.sourcesFor(record);
        } catch {
          continue;
        }
        if (this.disposed || this.questionOpen()) return;
        // A container delete, also through its root page, asks for its tables
        // in the same dialog and is sent as the container delete.
        const container =
          record.operation !== 'folder.permanentDelete' && sources.length === 1
            ? sources[0]
            : null;
        const chosen: { tables: AppBaseTableDispositionInput[] | null } = {
          tables: null,
        };
        const confirmed = await this.confirmContainers(
          sources,
          undefined,
          container
            ? {
                workspaceId: record.workspaceId,
                appBaseId: container.appBaseId,
                skipCopy:
                  record.payload.copyConfirmed &&
                  record.lastError === OFFLINE_DELETE_TABLE_CHOICE,
                onChosen: tables => {
                  chosen.tables = tables;
                },
              }
            : undefined
        );
        if (this.disposed) return;
        await this.outbox.removeFailed(record.id);
        if (confirmed && container && chosen.tables) {
          await this.outbox.enqueue({
            operation: 'appBase.permanentDelete',
            payload: {
              appBaseId: container.appBaseId,
              copyConfirmed: true,
              tables: chosen.tables,
            },
            idempotencyKey: `appBase.permanentDelete:${container.appBaseId}`,
            dedupePending: true,
            orderKey: `appBase:${container.appBaseId}`,
          });
        } else if (confirmed) {
          await this.outbox.enqueue({
            operation: record.operation,
            payload: { ...record.payload, copyConfirmed: true },
            idempotencyKey: record.idempotencyKey,
            dedupePending: true,
            orderKey: record.orderKey,
          });
        } else {
          toast(
            I18n.t(
              'com.streamlain.dataview.database.deletionPending.permanentCancelled'
            )
          );
        }
      }
    } finally {
      this.processing = false;
    }
  }
}
